import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Button, ButtonGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchCategories,
  deleteCategory,
} from "../../slice/ExpenseCategorySlice";
import {
  fetchExpenses,
  deleteExpense,
  clearError,
} from "../../slice/ExpenseSlice";
import NotifyData from "../../components/NotifyData";
import TableUI from "../../components/TableUI";
import { ActionButton } from "../../components/Buttons";
import CategoryModal from "./ExpenseCategory";
import ExpenseReport from "./ExpenseReport";
import "react-datepicker/dist/react-datepicker.css";
import { LiaEditSolid } from "react-icons/lia";
import { MdOutlineDelete } from "react-icons/md";
import {
  formatDate,
  CustomPagination,
} from "../../components/CustomPagination";
import { HiOutlineDotsVertical } from "react-icons/hi";

const Expense = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("category");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [categoryCurrentPage, setCategoryCurrentPage] = useState(1);
  const [expenseCurrentPage, setExpenseCurrentPage] = useState(1);
  const [categoryPageData, setCategoryPageData] = useState([]);
  const [expensePageData, setExpensePageData] = useState([]);
  const [isCategoriesFetched, setIsCategoriesFetched] = useState(false);
  const [isExpensesFetched, setIsExpensesFetched] = useState(false);

  const { categories = [] } = useSelector((state) => state.categories);
  const { expenses = [], error } = useSelector((state) => state.expense);

  const filteredCategories = useMemo(
    () =>
      categories?.filter((category) =>
        category?.category_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ) || [],
    [categories, searchTerm],
  );

  const filteredExpenses = useMemo(
    () =>
      selectedDate
        ? expenses.filter((exp) => exp.expense_date === selectedDate)
        : expenses,
    [expenses, selectedDate],
  );

  const categoryTotalPages = Math.ceil(
    filteredCategories.length / itemsPerPage,
  );
  const expenseTotalPages = Math.ceil(filteredExpenses.length / itemsPerPage);

  useEffect(() => {
    if (!isCategoriesFetched) {
      dispatch(fetchCategories({ searchText: "" })).finally(() =>
        setIsCategoriesFetched(true),
      );
    }

    if (activeTab === "expense" && !isExpensesFetched) {
      dispatch(fetchExpenses({ searchText: "" })).finally(() =>
        setIsExpensesFetched(true),
      );
    }
  }, [dispatch, activeTab, isCategoriesFetched, isExpensesFetched]);

  useEffect(() => {
    if (activeTab === "category" && isCategoriesFetched) {
      const delayDebounce = setTimeout(() => {
        dispatch(fetchCategories({ searchText: searchTerm }));
      }, 300);
      return () => clearTimeout(delayDebounce);
    }
  }, [dispatch, searchTerm, activeTab, isCategoriesFetched]);

  useEffect(() => {
    const startIdxCategory = (categoryCurrentPage - 1) * itemsPerPage;
    const endIdxCategory = startIdxCategory + itemsPerPage;
    const startIdxExpense = (expenseCurrentPage - 1) * itemsPerPage;
    const endIdxExpense = startIdxExpense + itemsPerPage;

    setCategoryPageData(
      filteredCategories.slice(startIdxCategory, endIdxCategory),
    );
    setExpensePageData(filteredExpenses.slice(startIdxExpense, endIdxExpense));
  }, [
    filteredCategories,
    filteredExpenses,
    categoryCurrentPage,
    expenseCurrentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    setCategoryCurrentPage(1);
  }, [filteredCategories]);

  useEffect(() => {
    setExpenseCurrentPage(1);
  }, [filteredExpenses]);

  const handleChange = (e) => setSelectedDate(e.target.value);

  const handleClearSearch = () => {
    if (activeTab === "category") {
      setSearchTerm("");
      dispatch(fetchCategories({ searchText: "" }));
    } else {
      setSelectedDate("");
    }
  };

  const openAddCategoryModal = () => {
    setEditingCategory(null);
    setShowCategoryModal(true);
  };

  const openEditCategoryModal = (category) => {
    setEditingCategory(category);
    setShowCategoryModal(true);
  };

  const handleCategoryModalClose = async (refresh = false) => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    if (refresh) {
      await dispatch(fetchCategories({ searchText: searchTerm })).unwrap();
    }
  };

  const handleCategoryDelete = async (categoryId) => {
    try {
      await dispatch(deleteCategory(categoryId)).unwrap();
      NotifyData("Category Deleted Successfully", "success");
      await dispatch(fetchCategories({ searchText: searchTerm })).unwrap();
    } catch (error) {
      NotifyData("Failed to delete category", "error");
    }
  };

  const handleExpenseCreate = () => {
    navigate("/expense/create");
  };

  const handleExpenseEdit = (expense) => {
    navigate(`/expense/edit/${expense.expense_id}`);
  };

  const handleExpenseDelete = async (expenseId) => {
    try {
      await dispatch(deleteExpense(expenseId)).unwrap();
      NotifyData("Expense Deleted Successfully", "success");
      await dispatch(fetchExpenses({ searchText: "" })).unwrap();
    } catch (error) {
      NotifyData("Failed to delete expense", "error");
    }
  };

  const handleCategoryPageChange = (page) => {
    setCategoryCurrentPage(page);
    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setCategoryPageData(filteredCategories.slice(start, end));
  };

  const handleExpensePageChange = (page) => {
    setExpenseCurrentPage(page);
    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setExpensePageData(filteredExpenses.slice(start, end));
  };

  const categoryTableHead = ["No", "Category Name"];
  const categoryTableData = (categoryPageData || []).map((category, index) => ({
    values: [
      index + 1,
      category.category_name,
      <ActionButton
        options={[
          {
            label: "Edit",
            icon: <LiaEditSolid />,
            onClick: () => openEditCategoryModal(category),
          },
          {
            label: "Delete",
            icon: <MdOutlineDelete />,
            onClick: () => handleCategoryDelete(category.category_id),
          },
        ]}
        label={<HiOutlineDotsVertical />}
      />,
    ],
  }));

  const expenseTableHead = ["No", "Date"];
  const expenseTableData = (expensePageData || []).map((expense, index) => ({
    values: [
      index + 1,
      formatDate(expense.expense_date),
      <ActionButton
        options={[
          {
            label: "Edit",
            icon: <LiaEditSolid />,
            onClick: () => handleExpenseEdit(expense),
          },
          {
            label: "Delete",
            icon: <MdOutlineDelete />,
            onClick: () => handleExpenseDelete(expense.expense_id),
          },
        ]}
        label={<HiOutlineDotsVertical />}
      />,
    ],
  }));

  return (
    <div id="main">
      <Container fluid>
        <Row className="pt-4 pb-4">
          <Col lg={8} xs={12}>
            <ButtonGroup className="w-100">
              <Button
                className={`tab-button ${activeTab === "category" ? "active" : ""}`}
                variant="light"
                onClick={() => setActiveTab("category")}
              >
                Category
              </Button>
              <Button
                className={`tab-button ${activeTab === "expense" ? "active" : ""}`}
                variant="light"
                onClick={() => setActiveTab("expense")}
              >
                Expense
              </Button>
              <Button
                className={`tab-button ${activeTab === "report" ? "active" : ""}`}
                variant="light"
                onClick={() => setActiveTab("report")}
              >
                Expense Report
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        {activeTab === "report" ? (
          <ExpenseReport />
        ) : (
          <>
            <Row className="align-items-center">
              <Col xs="12" className="text-end">
                <Button
                  variant="success"
                  className="submit-btn"
                  onClick={
                    activeTab === "expense"
                      ? handleExpenseCreate
                      : openAddCategoryModal
                  }
                >
                  Add New
                </Button>
              </Col>
            </Row>

            <Row>
              <Col
                xs={12}
                sm={6}
                md={4}
                lg={3}
                className="py-3 d-flex align-items-center gap-2"
              >
                {activeTab === "category" ? (
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search category"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                ) : (
                  <input
                    type="date"
                    className="form-control"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                  />
                )}
                {(searchTerm || selectedDate) && (
                  <Button variant="secondary" onClick={handleClearSearch}>
                    Clear
                  </Button>
                )}
              </Col>
              <Col
                xs={12}
                sm={6}
                md={4}
                lg={3}
                className="py-3 ms-auto align-items-center"
              >
                <CustomPagination
                  currentPage={
                    activeTab === "category"
                      ? categoryCurrentPage
                      : expenseCurrentPage
                  }
                  totalPages={
                    activeTab === "category"
                      ? categoryTotalPages
                      : expenseTotalPages
                  }
                  manualPageControl={true}
                  onPageChange={
                    activeTab === "category"
                      ? handleCategoryPageChange
                      : handleExpensePageChange
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col xs="12" className="py-3">
                {activeTab === "category" ? (
                  <TableUI
                    headers={categoryTableHead}
                    body={categoryPageData.length > 0 ? categoryTableData : []}
                  />
                ) : activeTab === "expense" ? (
                  <TableUI
                    headers={expenseTableHead}
                    body={expensePageData.length > 0 ? expenseTableData : []}
                  />
                ) : null}
              </Col>
            </Row>

            <CategoryModal
              show={showCategoryModal}
              handleClose={handleCategoryModalClose}
              editCategory={editingCategory}
            />
          </>
        )}
      </Container>
    </div>
  );
};

export default Expense;
